import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import jsQR from 'jsqr';
import type { Friend, Group, Identity } from '../types';
import { errorMessage } from '../lib/db';
import { buildInviteMessage, shareInvite } from '../lib/invite';
import { lookupByUsername } from '../lib/relay';
import { parseGroupShareUrl } from '../lib/groupShare';

type Props = {
  me: Identity;
  friends: Friend[];
  groups: Group[];
  onAddFriend: (f: Friend) => void;
  onAddGroup: (g: Group) => void;
  onBack: () => void;
};

type Tab = 'show' | 'scan';

export function QRAdd({ me, onAddFriend, onAddGroup, onBack }: Props) {
  const [tab, setTab] = useState<Tab>('show');
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shareLabel, setShareLabel] = useState<string>('Share invite link');

  // QR shape: `norush:<username>:<user_id>` (v2; pre-linked-devices used
  // `norush:<username>:<tweetnacl_pubkey>`). Joiner does a fresh device
  // lookup by user_id on scan so we don't bake stale keys into the QR.
  const qrPayload = `norush:${me.username}:${me.id}`;

  async function doShare() {
    try {
      const result = await shareInvite(me.username);
      if (result === 'shared') setShareLabel('Sent');
      else if (result === 'copied') setShareLabel('Copied — paste anywhere');
      else return;
      window.setTimeout(() => setShareLabel('Share invite link'), 2400);
    } catch {
      setShareLabel("Couldn't share. Long-press to copy.");
      window.setTimeout(() => setShareLabel('Share invite link'), 3200);
    }
  }

  useEffect(() => {
    QRCode.toDataURL(qrPayload, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 240,
      color: { dark: '#2b2a26', light: '#fbf9f4' },
    })
      .then(setQrDataUrl)
      .catch((err) => setError(errorMessage(err)));
  }, [qrPayload]);

  return (
    <div className="screen">
      <header className="topbar">
        <button className="btn btn-ghost btn-small" onClick={onBack} aria-label="Back">
          ← Back
        </button>
        <div className="topbar-center">
          <span className="me-name">Add by QR</span>
        </div>
        <span className="topbar-spacer" />
      </header>

      <main className="content">
        <div className="tab-row">
          <button
            className={`tab ${tab === 'show' ? 'tab-active' : ''}`}
            onClick={() => setTab('show')}
          >
            Your QR
          </button>
          <button
            className={`tab ${tab === 'scan' ? 'tab-active' : ''}`}
            onClick={() => setTab('scan')}
          >
            Scan
          </button>
        </div>

        {tab === 'show' && (
          <>
            <section className="card center">
              <h2 className="h-section">In person — show this to a friend.</h2>
              <p className="muted">
                Have them open No Rush, tap "Add by QR", switch to "Scan", and point
                their camera at this code. They'll be added to their friend list.
              </p>
              {error && <p className="error">{error}</p>}
              {qrDataUrl && (
                <img
                  src={qrDataUrl}
                  alt={`QR code for @${me.username}`}
                  className="qr-img"
                />
              )}
              <p className="me-name">@{me.username}</p>
              <p className="footnote">
                The QR contains your username and your public key. It does not
                contain anything secret.
              </p>
            </section>

            <section className="card center">
              <h2 className="h-section">Not in the same room?</h2>
              <p className="muted">
                Send a friend the invite by chat. The message includes your
                username so they know who to add back.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={doShare}
                style={{ marginTop: 8 }}
              >
                {shareLabel}
              </button>
              <p className="invite-preview" style={{ marginTop: 14 }}>
                "{buildInviteMessage(me.username)}"
              </p>
            </section>
          </>
        )}

        {tab === 'scan' && (
          <Scanner me={me} onAddFriend={onAddFriend} onAddGroup={onAddGroup} />
        )}
      </main>
    </div>
  );
}

type ScannerProps = {
  me: Identity;
  onAddFriend: (f: Friend) => void;
  onAddGroup: (g: Group) => void;
};

function Scanner({ me, onAddFriend, onAddGroup }: ScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<string>('Initializing camera…');
  const [found, setFound] = useState<
    | { kind: 'friend'; username: string; warning?: string }
    | { kind: 'group'; name: string; members: number }
    | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    let rafId: number | null = null;

    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setStatus('Point at a No Rush QR — a friend\'s, or a group invite.');
        scanLoop();
      } catch (err) {
        setStatus(
          err instanceof Error
            ? `Camera unavailable: ${err.message}. You can also paste a norush: URL below.`
            : 'Camera unavailable.',
        );
      }
    }

    function scanLoop() {
      if (cancelled) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
        rafId = requestAnimationFrame(scanLoop);
        return;
      }
      const w = video.videoWidth;
      const h = video.videoHeight;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, w, h);
      const imageData = ctx.getImageData(0, 0, w, h);
      const code = jsQR(imageData.data, w, h);
      if (code) {
        void handleDecoded(code.data);
        return;
      }
      rafId = requestAnimationFrame(scanLoop);
    }

    async function handleDecoded(data: string) {
      cancelled = true;
      stopStream();
      await processQrPayload(data);
    }

    void start();

    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      stopStream();
    };

    function stopStream() {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    }
  }, []);

  async function processQrPayload(payload: string) {
    // Group invite first — its prefix is longer than the friend URL prefix.
    const group = parseGroupShareUrl(payload);
    if (group) {
      // Drop the joiner's own self-entry if it happens to be in the
      // group spec (would normally not be, but be defensive).
      const members = group.members.filter((m) => m.id !== me.id);
      onAddGroup({
        id: group.id,
        name: group.name,
        members,
        created_at: new Date().toISOString(),
      });
      setFound({ kind: 'group', name: group.name, members: members.length + 1 });
      setStatus('');
      return;
    }

    const parsedFriend = parseFriendQrPayload(payload);
    if (!parsedFriend) {
      setStatus(`That QR isn't a No Rush code (decoded as: ${truncate(payload, 60)}).`);
      return;
    }
    if (parsedFriend.username === me.username) {
      setStatus("That's your own QR.");
      return;
    }
    setStatus(`Looking up @${parsedFriend.username}…`);
    try {
      const friend = await lookupByUsername(parsedFriend.username);
      if (!friend) {
        setStatus(`@${parsedFriend.username} isn't registered on the relay.`);
        return;
      }
      let warning: string | undefined;
      if (parsedFriend.user_id && friend.id !== parsedFriend.user_id) {
        warning =
          'The user id in the QR does not match the current owner of this ' +
          'username. They may have re-registered. Verify the safety number ' +
          'in person before sending anything sensitive.';
      }
      onAddFriend(friend);
      setFound({ kind: 'friend', username: friend.username, warning });
      setStatus('');
    } catch (err) {
      setStatus(errorMessage(err));
    }
  }

  async function onPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const data = e.clipboardData.getData('text');
    if (data?.startsWith('norush:')) {
      e.preventDefault();
      await processQrPayload(data);
    }
  }

  return (
    <section className="card">
      <h2 className="h-section">Scan a QR</h2>
      <p className="muted">{status}</p>

      {!found && (
        <>
          <div className="scanner-frame">
            <video ref={videoRef} className="scanner-video" muted playsInline />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
          <label className="field" style={{ marginTop: 18 }}>
            <span className="field-label">…or paste a norush: URL</span>
            <input
              type="text"
              placeholder="norush:username:base64pubkey or norush:group:..."
              onPaste={onPaste}
            />
          </label>
        </>
      )}

      {found?.kind === 'friend' && (
        <div className="found-card">
          <p className="h-section" style={{ margin: 0 }}>
            Added @{found.username}
          </p>
          {found.warning && <p className="error">{found.warning}</p>}
        </div>
      )}

      {found?.kind === 'group' && (
        <div className="found-card">
          <p className="h-section" style={{ margin: 0 }}>
            Joined group "{found.name}"
          </p>
          <p className="muted">
            {found.members} people · you'll see messages from any member;
            sending will fan out to all of them.
          </p>
        </div>
      )}
    </section>
  );
}

/**
 * QR shape: `norush:<username>:<user_id>`. The second field used to be a
 * base64 TweetNaCl pubkey in the pre-linked-devices schema; that's now
 * unused and we expect a uuid here. We accept both shapes for transient
 * compatibility while users update — but the warning logic at the call
 * site only fires when we successfully parsed a user_id.
 */
function parseFriendQrPayload(
  s: string,
): { username: string; user_id?: string } | null {
  if (!s.startsWith('norush:')) return null;
  if (s.startsWith('norush:group:')) return null;
  const rest = s.slice('norush:'.length);
  const idx = rest.indexOf(':');
  if (idx < 1) return null;
  const username = rest.slice(0, idx);
  const tail = rest.slice(idx + 1);
  if (!username || !tail) return null;
  // Recognize a uuid as user_id; otherwise tail is an old-format pubkey
  // we just ignore.
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    tail,
  );
  return { username, user_id: isUuid ? tail : undefined };
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n) + '…';
}
